import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TopBar from './components/TopBar.jsx';
import TopNav from './components/TopNav.jsx';
import NoteSheet from './components/NoteSheet.jsx';
import Login from './routes/Login.jsx';
import AuthCallback from './routes/AuthCallback.jsx';
import Overview from './routes/Overview.jsx';
import Today from './routes/Today.jsx';
import Calls from './routes/Calls.jsx';
import Deliverables from './routes/Deliverables.jsx';
import Meetings from './routes/Meetings.jsx';
import Reports from './routes/Reports.jsx';
import Project from './routes/Project.jsx';
import Settings from './routes/Settings.jsx';
import EditRequests from './routes/EditRequests.jsx';
import { session, apiFetch } from './lib/api.js';
import { NoteSheetProvider } from './context/NoteSheetContext.jsx';
import { ToastProvider } from './context/ToastContext.jsx';

/**
 * RequireAuth — layout route guard.
 * Renders <Outlet /> when authenticated; redirects to /login otherwise.
 */
function RequireAuth() {
  const location = useLocation();
  if (!session.token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <Outlet />;
}

/**
 * Ticker — horizontal scrolling strip under the topbar, showing live project
 * facts: business name, current stage, deliverables delivered count, and the
 * next check-in. Reads the same ['overview'] query Shell's prefetch and the
 * Overview screen already populate, so react-query serves it from cache —
 * no extra network round trip in the common case, and nothing here is
 * invented: every field comes straight from GET /api/console/overview.
 */
function Ticker() {
  const { data } = useQuery({
    queryKey: ['overview'],
    queryFn: () => apiFetch('/api/console/overview'),
  });

  if (!data) return null;

  const {
    business_name:      biz     = '',
    stage,
    deliverable_counts: counts  = {},
    deliverable_total:  total   = 0,
    next_meeting:       meeting = null,
  } = data;

  const delivered  = counts.delivered ?? 0;
  const stageLabel = stage ? stage.replace(/_/g, ' ').toUpperCase() : null;
  const nextCheckIn = meeting
    ? new Date(meeting.start_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        timeZone: 'America/Chicago',
      }) + ' CT'
    : 'NOT SCHEDULED';

  const items = [
    biz        ? { text: `PROJECT / ${biz}` } : null,
    stageLabel ? { text: `STAGE / ${stageLabel}`, acc: true } : null,
    { text: `DELIVERED / ${delivered} OF ${total}` },
    { text: `NEXT CHECK-IN / ${nextCheckIn}` },
  ].filter(Boolean);

  // Doubled so the ticker-in 50%-translate keyframe (global.css) loops seamlessly.
  const doubled = [...items, ...items];

  return (
    <div className="ticker" aria-hidden="true">
      <div className="ticker-in">
        {doubled.map((item, i) => (
          <span key={i} className={item.acc ? 'tk-acc' : undefined}>{item.text}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * Shell — stable layout rendered ONCE at the RequireAuth > Shell level.
 * A sticky top header (TopBar + Ticker), a desktop left rail / mobile bottom
 * bar (TopNav), and the NoteSheet stay mounted across all tab transitions.
 * Content lives in the .content column (global.css: left-offset for the
 * rail on desktop, full-width with bottom padding for the bbar on mobile).
 *
 * Loading-flash fix: on mount we prefetch every tab's data into the react-query
 * cache, so switching tabs reads cache instantly instead of flashing a skeleton.
 */
function Shell() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Warm the cache for all tabs in the background (idle), so the first visit
    // to each tab is instant. staleTime (60s, set in main.jsx) keeps them fresh.
    const prefetch = [
      ['overview',      '/api/console/overview'],
      ['calls',         '/api/console/calls?limit=50'],
      ['edit-requests', '/api/console/edit-requests'],
      ['deliverables',  '/api/console/deliverables'],
      ['meetings',     '/api/console/meetings'],
      ['slots',        '/api/console/booking/slots'],
      ['reports',      '/api/console/reports'],
    ];
    const run = () => prefetch.forEach(([key, path]) =>
      queryClient.prefetchQuery({ queryKey: [key], queryFn: () => apiFetch(path) }));
    const id = (window.requestIdleCallback || ((cb) => setTimeout(cb, 200)))(run);
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, [queryClient]);

  return (
    <div className="gridbg" style={{ minHeight: '100%' }}>
      {/* Skip-to-content: keyboard/screen-reader a11y (§8) */}
      <a
        href="#main-content"
        style={{
          position:      'absolute',
          left:          '-9999px',
          top:           '8px',
          zIndex:        100,
          padding:       '8px 16px',
          background:    'var(--acc)',
          color:         '#16130E',
          fontSize:      '14px',
          fontWeight:    600,
          fontFamily:    'var(--font-body)',
          textDecoration:'none',
        }}
        onFocus={(e) => { e.currentTarget.style.left = '8px'; }}
        onBlur={(e)  => { e.currentTarget.style.left = '-9999px'; }}
      >
        Skip to content
      </a>

      {/* ── Sticky header: TopBar + Ticker, one shared stacking container ──── */}
      <header role="banner" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <TopBar />
        <Ticker />
      </header>

      {/* Desktop left rail + mobile bottom tab bar — same nav/routing state */}
      <TopNav />

      <main id="main-content" className="content screen">
        <Outlet />
      </main>

      {/* Note sheet — inside the Shell's stacking context, above all content. */}
      <NoteSheet />
    </div>
  );
}

/**
 * App — root router.
 *
 * Providers wrapping order (outer → inner):
 *   NoteSheetProvider — open/close state for the note sheet
 *   ToastProvider     — toast queue + rendering
 *
 * Route tree:
 *   /login          → Login (no auth, no shell)
 *   /auth/callback  → AuthCallback (no auth, no shell)
 *   RequireAuth (layout — guards everything below)
 *     Shell (layout — stable top header: TopBar + TopNav + NoteSheet)
 *       /             → Overview
 *       /deliverables → Deliverables
 *       /meetings     → Meetings
 *       /reports      → Reports
 *   *               → redirect to /
 */
export default function App() {
  return (
    <NoteSheetProvider>
      <ToastProvider>
        <Routes>
          {/* Public auth routes — no shell */}
          <Route path="/login"         element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected routes — auth guard → shell → screens */}
          <Route element={<RequireAuth />}>
            <Route element={<Shell />}>
              <Route path="/"             element={<Overview />} />
              {/* Today was built and wired to GET /api/console/today, then never
                  routed — it is the one screen that answers "did I miss money
                  last night", which is the question this ICP actually opens the
                  console for. Added at /today rather than replacing "/" because
                  making it the home screen is a product decision, not a fix. */}
              <Route path="/today"        element={<Today />} />
              <Route path="/calls"        element={<Calls />} />
              <Route path="/edits"        element={<EditRequests />} />
              <Route path="/deliverables" element={<Deliverables />} />
              <Route path="/meetings"     element={<Meetings />} />
              <Route path="/reports"      element={<Reports />} />
              {/* Reachable via in-app navigation (Overview action band + TopBar),
                  not the primary nav rail — keeps the rail to the 5 daily tabs. */}
              <Route path="/project"      element={<Project />} />
              <Route path="/settings"     element={<Settings />} />
            </Route>
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </NoteSheetProvider>
  );
}
